<script context="module" lang="ts">
    /**
     * Pure layout helpers, exported so the width-dependent geometry can be
     * unit tested without mounting the component: jsdom never reports a real
     * clientWidth (no ResizeObserver), so `cw` below stays 0 in every test,
     * and a full render can only ever exercise the 900-fallback layout.
     */

    /**
     * The viewBox width to lay out with. In a real browser this is the
     * chart's own clientWidth in CSS pixels, so text and gutters render at a
     * fixed, legible size instead of shrinking with the column the chart
     * sits in (the previous fixed 900-unit viewBox let an 11px label render
     * as small as 4px in a phone-width column, since the whole SVG, text
     * included, scaled by containerWidth / 900). A measurement of 0 or less
     * (jsdom, or before the first layout pass) falls back to the historical
     * 900, which is what every geometry test in this file still expects.
     */
    export function chartViewportWidth(measuredWidth: number, fallback = 900, min = 300): number {
        return measuredWidth > 0 ? Math.max(min, measuredWidth) : fallback;
    }

    /**
     * The longest category label, in characters, plus one character of
     * breathing room, floored at minChars. Vertical category-label thinning
     * used to assume every label was at most minChars wide (sized for the
     * 5-character week labels StackedBars first shipped with), so a
     * genuinely longer label, such as a propensity bucket ("0.21 to 0.40",
     * 12 characters), could still collide with its neighbour after thinning
     * once text stopped shrinking away with the container.
     */
    export function longestCategoryChars(categories: string[], minChars: number): number {
        return categories.reduce((m, c) => Math.max(m, c.length + 1), minChars);
    }

    /**
     * Keep every Nth vertical category label once a band is too narrow to
     * fit the widest label at fontPx, using a rough average glyph width of
     * 0.6em.
     */
    export function computeLabelStride(bandWidth: number, longestChars: number, fontPx: number): number {
        if (bandWidth <= 0) return 1;
        return Math.max(1, Math.ceil((longestChars * fontPx * 0.6) / bandWidth));
    }

    /**
     * Horizontal mode's left gutter for category labels, capped to a share
     * of the chart width so it cannot crowd out the plot at phone widths.
     */
    export function horizontalLabelGutter(preferred: number, vw: number, maxShare = 0.3): number {
        return Math.min(preferred, vw * maxShare);
    }

    /**
     * Per-character width estimate for the wrap decision, as a share of
     * fontPx. computeLabelStride's 0.6em is deliberately conservative (its
     * job is only to decide when to hide an inferable week label, so
     * overestimating costs nothing); reused for wrapping, that same 0.6em
     * overestimates this app's actual font (Public Sans) by 30%+ on ordinary
     * words ("history" at 11px measures 35px in Chromium, versus 0.6em's
     * 46px), which wrongly forced a bucket label like "No history" to fall
     * back to thinning on a real phone-width band it would easily have
     * wrapped into. 0.5em stays a safe over-estimate against every category
     * label this app currently draws (verified against real measured glyph
     * widths for the propensity-bucket and age-range vocabularies) while
     * fitting the bands their real containers measure out to.
     */
    const WRAP_CHAR_W_EM = 0.5;

    /**
     * Extra px of breathing room required before two words are allowed to
     * share one wrapped line (R1 second regression). Applied only to that
     * *join* decision, never to whether a single, already-unsplittable word
     * fits at all: a lone word ("history" left over after "No" splits off,
     * or a real week token like "9/14") has no narrower alternative, so
     * demanding extra margin there would only convert a real, displayable
     * label into a dropped one. Guarding the join instead is free (worst
     * case it costs one more of the 3 available lines) and is exactly what
     * stops a near-miss merge like "0.01 to" (estimated at 38.5px of a
     * 41.33px real propensity-bucket band, a 2.8px margin) from landing
     * close enough to its neighbour's own wrapped text to look like it's
     * touching.
     */
    const WRAP_JOIN_MIN_GAP_PX = 6;

    /**
     * Wrap one vertical category label at spaces onto at most maxLines lines,
     * each estimated no wider than bandWidth at fontPx * charWidthEm. Returns
     * null when there is nothing left to break at, i.e. a single word alone
     * is already wider than the band (real week labels like "9/14" at phone
     * widths never have a space to wrap at), or when even the greediest wrap
     * still needs more than maxLines lines: both cases tell the caller to
     * fall back to thinning instead of wrapping.
     *
     * A line that is not the label's last keeps the trailing space the
     * original text had there, so joining every returned line back together
     * (as a rendered <text>'s full textContent does, once the lines become
     * sibling <tspan>s) reproduces the original label exactly rather than
     * running the last two words together.
     */
    export function wrapCategoryLabel(
        label: string,
        bandWidth: number,
        fontPx: number,
        maxLines = 3,
        charWidthEm = WRAP_CHAR_W_EM,
        joinMinGap = WRAP_JOIN_MIN_GAP_PX,
    ): string[] | null {
        if (bandWidth <= 0) return null;
        const charW = fontPx * charWidthEm;
        const fitsAlone = (s: string) => s.length * charW <= bandWidth;
        const fitsJoined = (s: string) => s.length * charW + joinMinGap <= bandWidth;
        const words = label.split(' ').filter(w => w.length > 0);
        if (words.length === 0) return [label];
        if (words.some(w => !fitsAlone(w))) return null;
        const lines: string[] = [];
        let line = words[0];
        for (let i = 1; i < words.length; i++) {
            const joined = `${line} ${words[i]}`;
            if (fitsJoined(joined)) {
                line = joined;
            } else {
                lines.push(`${line} `);
                line = words[i];
            }
        }
        lines.push(line);
        return lines.length <= maxLines ? lines : null;
    }

    /**
     * Decide how to draw every vertical category label at a band width.
     * Round 1's thinning decision (longestCategoryChars / computeLabelStride)
     * is always the first step, and is the *only* step whenever it comes back
     * stride 1: every label draws on one line, exactly as round 1, with no
     * wrap attempt at all. This is what fixes the second R1 regression, where
     * a single-token week label like "4/27" happened to fit its own (tiny)
     * band as a trivial one-line "wrap", so the whole 26-week axis stopped
     * thinning; a single token has no space to wrap at in the first place, so
     * it can never legitimately need anything other than round 1's own
     * stride.
     *
     * Only once that first step comes back stride > 1 (the band is too
     * narrow for the longest label) *and* at least one category contains a
     * space does wrapping (Problem 1) get a chance: bucket-style categories
     * ("No history", "0.61 to 0.80", ...) all draw instead of some being
     * thinned away, since there is no "neighbour" to infer a hidden bucket
     * from the way there is for an omitted week. The wrap is accepted only
     * if every category wraps to at most 3 lines; otherwise this falls back
     * to round 1's stride untouched, same as when no category has a space at
     * all (e.g. the 26-week activity axis, every label a single token).
     */
    export function layoutCategoryLabels(
        categories: string[],
        bandWidth: number,
        fontPx: number,
        minChars: number,
    ): { lines: string[][]; stride: number } {
        const longest = longestCategoryChars(categories, minChars);
        const stride = computeLabelStride(bandWidth, longest, fontPx);
        if (stride <= 1) {
            return { lines: categories.map(c => [c]), stride: 1 };
        }
        if (categories.some(c => c.includes(' '))) {
            const wrapped = categories.map(c => wrapCategoryLabel(c, bandWidth, fontPx));
            if (wrapped.every((w): w is string[] => w !== null)) {
                return { lines: wrapped, stride: 1 };
            }
        }
        return { lines: categories.map(c => [c]), stride };
    }

    /** One stacked segment's pixel extent along the stacking direction of one
     * bar: its offset from the bar's own start and its own thickness, both
     * already converted to px the same way the segment rects themselves are
     * (value / max * plotSize). Ordered stacking-first-to-last, i.e. the same
     * order `segments()` below produces (bottom-up for vertical bars,
     * left-to-right for horizontal ones), with no gap between consecutive
     * entries: extents[i].start + extents[i].length === extents[i + 1].start.
     */
    export interface SegmentExtent {
        start: number;
        length: number;
    }

    /**
     * Positions (in the same px space as `extents`) at which to draw a
     * boundary line between two adjacent stacked segments of one bar: only
     * where BOTH neighbours are at least minThickness px thick. Problem 2: a
     * 1px stroke drawn on every segment rect straddles its edges, eating
     * about 0.5px into a segment from each side, more once a neighbour's own
     * stroke eats in too, which nearly erased anything thinner than a few px
     * (Text outreach's "Opted out" at 1.31px fell from a visibility score of
     * 0.63 to 0.22; Propensity's "Lean REP" at 1.26px, 0.77 to 0.36). Drawing
     * separators as their own lines instead, only between two segments thick
     * enough to spare the space, leaves every thin segment at full fill with
     * nothing eating into it, while normal-sized neighbours still get a
     * clear 1px separator between them.
     */
    export function segmentSeparatorPositions(
        extents: SegmentExtent[],
        minThickness = 3,
    ): number[] {
        const positions: number[] = [];
        for (let i = 0; i < extents.length - 1; i++) {
            const a = extents[i];
            const b = extents[i + 1];
            if (a.length >= minThickness && b.length >= minThickness) {
                positions.push(a.start + a.length);
            }
        }
        return positions;
    }
</script>

<script lang="ts">
    /**
     * Stacked bar chart in plain SVG. No charting library: the bundle stays
     * small and the output is assertable in jsdom.
     *
     * Fonts and spacing come from tokens.css. There are no width tokens there,
     * so the chart lays out in its own real clientWidth (see `cw` below),
     * which is what lets it scale to whatever column it is dropped into
     * without its text shrinking along with it.
     */
    export let categories: string[] = [];
    export let series: { label: string; values: number[]; color: string }[] = [];
    export let orientation: 'vertical' | 'horizontal' = 'vertical';
    export let valueLabel = 'Voters';
    export let title = '';

    let cw = 0; // real container width in CSS px, via bind:clientWidth below
    $: VW = chartViewportWidth(cw); // viewBox width

    const BAR_FILL = 0.72; // share of a band the bar occupies
    const AXIS_L = 64;     // left gutter for value ticks, vertical mode
    const CAT_L = 132;     // left gutter for category labels, horizontal mode
    const PAD_R = 16;
    const PAD_T = 12;
    const CAP_H = 18;      // band reserved above the vertical plot for the caption (F1)
    const V_PLOT_H = 240;
    const H_BAND = 26;
    const CAT_FONT_PX = 11; // matches .cat in the style block below
    const MIN_LABEL_CHARS = 6; // floor under the longest-label stride below, for short label sets
    const CAT_LINE_STEP = 13; // px between wrapped category-label lines (F1 Problem 1)

    function niceMax(m: number): number {
        if (m <= 0) return 1;
        const p = Math.pow(10, Math.floor(Math.log10(m)));
        return Math.ceil(m / p) * p;
    }

    function fmt(v: number): string {
        return v.toLocaleString();
    }

    $: n = categories.length;
    $: totals = categories.map((_, i) => series.reduce((sum, s) => sum + (s.values[i] ?? 0), 0));
    $: max = niceMax(Math.max(0, ...totals));
    // Values here are always whole counts (voters, contacts), so every tick
    // label must read as a whole number too: round the midpoint for display
    // rather than let it land on a fraction like "3.5". Dedupe afterwards:
    // niceMax returns 1 for any max <= 1, so the rounded midpoint and the
    // max would otherwise both be "1" and render the same gridline twice.
    $: ticks = [...new Set([0, Math.round(max / 2), max])];

    $: vBand = n ? (VW - AXIS_L - PAD_R) / n : 0;
    $: vTop = PAD_T + CAP_H; // vertical plot's top, leaving the caption its own band (F1)

    // Vertical category labels collide once the band gets narrower than the
    // longest label needs (26 weeks on the activity chart, ~31px per band, or
    // a 12-character propensity bucket label). Wrap each label onto up to 3
    // lines when that lets every category stay on the axis (bucket names);
    // fall back to the pre-existing thin-to-every-kth-label, newest-anchored
    // behaviour only when a label cannot be wrapped to fit, e.g. a single
    // un-splittable week label ("9/14") wider than the band (Problem 1).
    $: labelLayout = layoutCategoryLabels(categories, vBand, CAT_FONT_PX, MIN_LABEL_CHARS);
    $: labelStride = labelLayout.stride;
    $: maxLabelLines = labelLayout.lines.reduce((m, l) => Math.max(m, l.length), 1);
    // Grow the plot for the extra wrapped lines so they never collide with
    // the legend below or get clipped by the SVG's own viewBox.
    $: vHeight = vTop + V_PLOT_H + 46 + (maxLabelLines - 1) * CAT_LINE_STEP;
    $: catL = horizontalLabelGutter(CAT_L, VW); // capped so it can't crowd out the plot, phone widths
    $: hHeight = PAD_T + n * H_BAND + 44;
    $: hPlotW = VW - catL - PAD_R;

    /** Stacked offsets for one category, zero-height segments dropped. */
    function segments(
        list: { label: string; values: number[]; color: string }[],
        i: number,
    ) {
        const out: { label: string; value: number; color: string; start: number }[] = [];
        let acc = 0;
        for (const s of list) {
            const v = s.values[i] ?? 0;
            if (v > 0) out.push({ label: s.label, value: v, color: s.color, start: acc });
            acc += v;
        }
        return out;
    }

    // One segment list per category, derived reactively so the bars repaint when
    // `series` changes even if `categories` does not. `series` is passed in
    // rather than closed over on purpose: a Svelte reactive statement depends
    // only on the identifiers it names, so `segments(i)` reading `series` from
    // the closure would leave `segs` stale after a series-only prop change and
    // calling `segments(i)` straight from the template would never mark the
    // inner each block dirty at all. Checked against svelte 4.2.20.
    $: segs = categories.map((_, i) => segments(series, i));

    /** Boundary y's (svg coords) to draw a separator at, for one vertical bar. */
    function verticalSeparators(
        segList: { start: number; value: number }[],
        maxV: number,
        plotH: number,
        top: number,
    ): number[] {
        const extents = segList.map(s => ({ start: (s.start / maxV) * plotH, length: (s.value / maxV) * plotH }));
        return segmentSeparatorPositions(extents).map(b => top + plotH - b);
    }

    /** Boundary x's (svg coords) to draw a separator at, for one horizontal bar. */
    function horizontalSeparators(
        segList: { start: number; value: number }[],
        maxV: number,
        plotW: number,
        left: number,
    ): number[] {
        const extents = segList.map(s => ({ start: (s.start / maxV) * plotW, length: (s.value / maxV) * plotW }));
        return segmentSeparatorPositions(extents).map(b => left + b);
    }

    // Same reasoning as `segs` above: name every dependency (segs, max,
    // V_PLOT_H/hPlotW, vTop/catL) directly in the $: expression so Svelte's
    // static dependency tracker picks all of them up, not just `segs`.
    $: vSepYs = segs.map(s => verticalSeparators(s, max, V_PLOT_H, vTop));
    $: hSepXs = segs.map(s => horizontalSeparators(s, max, hPlotW, catL));
</script>

<figure class="chart" bind:clientWidth={cw}>
    {#if orientation === 'vertical'}
        <svg viewBox={`0 0 ${VW} ${vHeight}`} role="img" aria-label={title}>
            <text class="cap" x="0" y="10">{valueLabel}</text>
            {#each ticks as t}
                <line
                    class="grid"
                    x1={AXIS_L}
                    x2={VW - PAD_R}
                    y1={vTop + V_PLOT_H - (t / max) * V_PLOT_H}
                    y2={vTop + V_PLOT_H - (t / max) * V_PLOT_H} />
                <text
                    class="tick"
                    x={AXIS_L - 8}
                    y={vTop + V_PLOT_H - (t / max) * V_PLOT_H + 4}
                    text-anchor="end">{fmt(t)}</text>
            {/each}
            {#each categories as cat, i}
                {#each segs[i] as seg}
                    <rect
                        class="segment"
                        x={AXIS_L + i * vBand + (vBand * (1 - BAR_FILL)) / 2}
                        width={vBand * BAR_FILL}
                        y={vTop + V_PLOT_H - ((seg.start + seg.value) / max) * V_PLOT_H}
                        height={(seg.value / max) * V_PLOT_H}
                        fill={seg.color}><title>{seg.label}: {fmt(seg.value)}</title></rect>
                {/each}
                {#each vSepYs[i] as sy}
                    <line
                        class="separator"
                        x1={AXIS_L + i * vBand + (vBand * (1 - BAR_FILL)) / 2}
                        x2={AXIS_L + i * vBand + (vBand * (1 - BAR_FILL)) / 2 + vBand * BAR_FILL}
                        y1={sy}
                        y2={sy} />
                {/each}
                {#if (n - 1 - i) % labelStride === 0}
                    <text class="cat" x={AXIS_L + i * vBand + vBand / 2} y={vTop + V_PLOT_H + 16} text-anchor="middle">{#each labelLayout.lines[i] as line, li}<tspan x={AXIS_L + i * vBand + vBand / 2} dy={li === 0 ? 0 : CAT_LINE_STEP}>{line}</tspan>{/each}</text>
                {/if}
            {/each}
        </svg>
    {:else}
        <svg viewBox={`0 0 ${VW} ${hHeight}`} role="img" aria-label={title}>
            {#each categories as cat, i}
                {#each segs[i] as seg}
                    <rect
                        class="segment"
                        x={catL + (seg.start / max) * hPlotW}
                        width={(seg.value / max) * hPlotW}
                        y={PAD_T + i * H_BAND + 3}
                        height={H_BAND - 8}
                        fill={seg.color}><title>{seg.label}: {fmt(seg.value)}</title></rect>
                {/each}
                {#each hSepXs[i] as sx}
                    <line
                        class="separator"
                        x1={sx}
                        x2={sx}
                        y1={PAD_T + i * H_BAND + 3}
                        y2={PAD_T + i * H_BAND + 3 + (H_BAND - 8)} />
                {/each}
                <text
                    class="cat"
                    x={catL - 8}
                    y={PAD_T + i * H_BAND + H_BAND / 2 + 2}
                    text-anchor="end">{cat}</text>
            {/each}
            {#each ticks as t, i}
                <text
                    class="tick"
                    x={catL + (t / max) * hPlotW}
                    y={PAD_T + n * H_BAND + 18}
                    text-anchor={i === ticks.length - 1 ? 'end' : 'middle'}>{fmt(t)}</text>
            {/each}
            <text class="cap" x={catL} y={PAD_T + n * H_BAND + 34}>{valueLabel}</text>
        </svg>
    {/if}
    <ul class="legend">
        {#each series as s}
            <li><span class="swatch" style="background: {s.color}"></span>{s.label}</li>
        {/each}
    </ul>
</figure>

<style>
    .chart {
        margin: 0;
        width: 100%;
    }
    svg {
        width: 100%;
        height: auto;
        display: block;
        font-family: var(--font-body);
    }
    .grid {
        stroke: var(--rule);
        stroke-width: 1;
    }
    .tick,
    .cat,
    .cap {
        fill: var(--ink-muted);
        font-size: 11px;
    }
    .cap {
        font-size: 10px;
        text-transform: uppercase;
        letter-spacing: 0.1em;
    }
    .separator {
        stroke: var(--paper);
        stroke-width: 1;
        vector-effect: non-scaling-stroke;
    }
    .legend {
        list-style: none;
        display: flex;
        flex-wrap: wrap;
        gap: var(--s-2) var(--s-4);
        margin: var(--s-2) 0 0;
        padding: 0;
        font-size: 0.78rem;
        color: var(--ink-soft);
    }
    .legend li {
        display: inline-flex;
        align-items: center;
        gap: var(--s-1);
    }
    .swatch {
        width: 10px;
        height: 10px;
        border-radius: 2px;
        display: inline-block;
    }
</style>
